import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, ChevronDown, ShieldCheck, HardHat, HeartPulse, Sprout, Landmark } from "lucide-react";
import { useState } from "react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { getServiceBySlug, COMPANY, SERVICES } from "@/data/services";
import { SITE_URL } from "@/config/site";

const ICONS = { HardHat, HeartPulse, ShieldCheck, Sprout, Landmark };

export default function ServicePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!service) return <Navigate to="/" replace />;

  const Icon = ICONS[service.icon] || ShieldCheck;
  const otherServices = SERVICES.filter(s => s.slug !== service.slug);

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        canonical={`${SITE_URL}/${service.slug}`}
        image={service.image}
      />

      {/* HERO */}
      <section className="relative bg-cream overflow-hidden" data-testid={`service-hero-${service.slug}`}>
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-12 lg:pt-20 pb-16 lg:pb-24 relative">
          <nav className="text-xs text-forest/60 mb-8 font-outfit" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-terracotta">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/#services" className="hover:text-terracotta">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-forest">{service.title}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-soft rounded-full mb-6">
                <Icon className="w-4 h-4 text-terracotta" />
                <span className="font-outfit text-xs tracking-[0.2em] uppercase text-forest/80">{service.hero.eyebrow}</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.1] tracking-tight">
                {service.hero.title}
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-forest/70 leading-relaxed max-w-xl">
                {service.hero.subtitle}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="#devis" data-testid="service-cta-quote" className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-[#1E3F73] text-white rounded-full px-8 py-4 font-outfit font-medium shadow-md hover:shadow-lg transition-all">
                  Devis gratuit <ArrowRight className="w-4 h-4" />
                </a>
                <a href={`tel:${COMPANY.phone}`} data-testid="service-cta-call" className="inline-flex items-center justify-center gap-2 bg-white border border-soft text-forest hover:border-forest rounded-full px-8 py-4 font-outfit font-medium">
                  <Phone className="w-4 h-4" /> Nous appeler
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img src={service.image} alt={service.title} className="rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 lg:py-28 bg-white" data-testid="service-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="max-w-2xl mb-14">
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Vos avantages</div>
            <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight">Ce que vous gagnez avec Perspective.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.benefits.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`} className="bg-cream p-6 rounded-2xl border border-soft">
                <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center mb-4">
                  <Check className="w-5 h-5 text-terracotta" />
                </div>
                <h3 className="font-outfit font-semibold text-forest mb-2">{b.title}</h3>
                <p className="text-sm text-forest/70 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="service-coverage">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Garanties</div>
            <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight mb-6">{service.coversTitle}</h2>
            <ul className="space-y-4">
              {service.covers.map((c, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`cover-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-forest text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-forest/80">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-3xl border border-soft">
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-3">Pour qui ?</div>
            <h3 className="font-display text-2xl text-forest mb-4">Profils concernés</h3>
            <p className="text-forest/75 leading-relaxed">{service.audience}</p>
            <a href="#devis" className="mt-8 inline-flex items-center gap-2 text-terracotta font-outfit font-medium hover:gap-3 transition-all">
              Vérifier mon éligibilité <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="service-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="text-center mb-14">
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Questions fréquentes</div>
            <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight">Tout savoir sur {service.title.toLowerCase()}.</h2>
          </div>
          <div className="space-y-3">
            {service.faq.map((item, i) => (
              <div key={i} className="bg-cream border border-soft rounded-2xl overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full px-6 py-5 flex justify-between items-center text-left"
                >
                  <span className="font-outfit font-semibold text-forest pr-4">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-terracotta flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-forest/75 leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="devis" className="py-20 lg:py-28 bg-forest text-white relative overflow-hidden" data-testid="service-quote-section">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 relative">
          <div>
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Devis gratuit</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight mb-6">
              Recevez votre proposition <span className="italic">{service.title.toLowerCase()}</span>.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Aucun engagement, aucune information à votre insu : juste un devis clair, adapté à votre situation.
            </p>
            <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 text-white hover:text-terracotta">
              <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
            </a>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-3xl shadow-2xl">
            <ContactForm defaultService={service.title} source={`service-${service.slug}`} />
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="other-services">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Découvrir aussi</div>
          <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight mb-12">Nos autres expertises.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {otherServices.map(s => {
              const OIcon = ICONS[s.icon] || ShieldCheck;
              return (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`other-service-${s.slug}`} className="group bg-white p-6 rounded-2xl border border-soft hover:border-terracotta hover:shadow-lg transition-all">
                  <div className="w-10 h-10 rounded-xl bg-forest text-white flex items-center justify-center mb-4 group-hover:bg-terracotta transition-colors">
                    <OIcon className="w-5 h-5" />
                  </div>
                  <h3 className="font-outfit font-semibold text-forest mb-2">{s.title}</h3>
                  <p className="text-sm text-forest/60">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
